// bluaIQ — M16: Progress Photos Module
// Phase-tagged photo uploads with offline PWA support

import { apiFetch } from '../utils/api'
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Camera, Upload, Image, Wifi, WifiOff, Trash2, Pencil, X, Eye, EyeOff } from 'lucide-react'
import imageCompression from 'browser-image-compression'
import { isDemoProject } from '../utils/demoData'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'

/**
 * Build a fully-qualified URL for a photo stored on the server volume.
 * storagePath from the API looks like "projectId/hash.jpg"; the server serves
 * those at /api/uploads/<storagePath>. Demo URLs (http[s]://…) are returned as-is.
 */
function buildPhotoUrl(storagePath?: string, token?: string | null): string {
  if (!storagePath) return ''
  if (/^https?:\/\//i.test(storagePath)) return storagePath
  if (storagePath.startsWith('/api/')) return storagePath
  const base = `/api/uploads/${storagePath.replace(/^\/+/, '')}`
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

// ── TYPES ─────────────────────────────────────────

interface Photo {
  id: string
  storagePath: string
  thumbnailPath?: string
  phase?: string
  description?: string
  createdAt: string
  // Portal visibility — default CLOSED; only the builder marks it (Maria 2026-07-30).
  clientVisible?: boolean
}

const PHASE_OPTIONS = [
  { value: '0', en: 'Pre-development', es: 'Pre-desarrollo' },
  { value: '1', en: 'Design & Permits', es: 'Diseño y Permisos' },
  { value: '2', en: 'Contracting', es: 'Contratación' },
  { value: '3', en: 'Construction', es: 'Construcción' },
  { value: '4', en: 'Closing & Sale', es: 'Cierre y Venta' },
]

const PHASE_COLORS: Record<string, string> = { '0': '#06B6D4', '1': '#3B82F6', '2': '#00BCD4', '3': '#14B8A6', '4': '#EC4899' }

/**
 * Resolve a stored phase code (e.g. '3') to a human-readable
 * label in the active language. Returns an empty string for
 * unknown codes so the badge can be conditionally hidden — we
 * never surface raw codes like "F3" to end users.
 */
function getPhaseLabel(phase: string | undefined | null, isEs: boolean): string {
  if (!phase) return ''
  const opt = PHASE_OPTIONS.find((p) => p.value === phase)
  if (!opt) return ''
  return isEs ? opt.es : opt.en
}

// ── DEMO DATA ───────────────────────────────────

const DEMO_PHOTOS: Photo[] = [
  { id: 'ph-1', storagePath: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=400&fit=crop', phase: '0', description: 'Lot cleared and graded — ready for foundation', createdAt: '2026-01-18T00:00:00Z' },
  { id: 'ph-2', storagePath: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=400&fit=crop', phase: '0', description: 'Soil test drilling — expansive clay identified', createdAt: '2026-01-22T00:00:00Z' },
  { id: 'ph-3', storagePath: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=400&h=400&fit=crop', phase: '3', description: 'Foundation forms set before pour', createdAt: '2026-02-25T00:00:00Z' },
  { id: 'ph-4', storagePath: 'https://images.unsplash.com/photo-1590644365607-1c5e64e4e1f6?w=400&h=400&fit=crop', phase: '3', description: 'Foundation pour complete — 42 cu yd concrete', createdAt: '2026-02-28T00:00:00Z' },
  { id: 'ph-5', storagePath: 'https://images.unsplash.com/photo-1513467535987-fd81bc7d62f8?w=400&h=400&fit=crop', phase: '3', description: 'Framing — first floor walls up', createdAt: '2026-03-05T00:00:00Z' },
  { id: 'ph-6', storagePath: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400&h=400&fit=crop', phase: '3', description: 'Framing complete — roof trusses set', createdAt: '2026-03-14T00:00:00Z' },
  { id: 'ph-7', storagePath: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=400&fit=crop', phase: '3', description: 'Plumbing rough-in started', createdAt: '2026-03-20T00:00:00Z' },
  { id: 'ph-8', storagePath: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=400&h=400&fit=crop', phase: '3', description: 'Electrical rough — panel box installed', createdAt: '2026-03-22T00:00:00Z' },
]

// ── COMPONENT ─────────────────────────────────────

export default function PhotosModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const isEs = i18n.language === 'es'

  const [uploadToken, setUploadToken] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [uploadPhase, setUploadPhase] = useState('3')
  const [isOnline] = useState(navigator.onLine)

  // Detail-modal state: clicking/tapping a card opens a
  // full-photo modal that shows the image at large size, the
  // full description (no line-clamp), the phase label, the date,
  // and Edit/Delete/Close buttons that work on both desktop and
  // mobile (no hover-only affordances).
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)

  // Edit state: holds the photo currently being edited (null when
  // the inline editor is closed). Description/phase live alongside
  // so the editor can be controlled. editError surfaces server
  // failures inline without dismissing the editor.
  const [editingPhoto, setEditingPhoto] = useState<Photo | null>(null)
  const [editDescription, setEditDescription] = useState('')
  const [editPhase, setEditPhase] = useState('3')
  const [editError, setEditError] = useState<string | null>(null)

  const startEditPhoto = (photo: Photo) => {
    setEditingPhoto(photo)
    setEditDescription(photo.description || '')
    setEditPhase(photo.phase || '3')
    setEditError(null)
  }
  const cancelEditPhoto = () => {
    setEditingPhoto(null)
    setEditError(null)
  }

  const { data: apiPhotos, isLoading } = useQuery<Photo[]>({
    queryKey: ['photos', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/photos`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })
  const isDemo = isDemoProject(projectId)
  const photos = Array.isArray(apiPhotos) ? apiPhotos : (isDemo ? DEMO_PHOTOS : [])

  useEffect(() => {
    let alive = true
    getToken()
      .then((token) => { if (alive) setUploadToken(token || null) })
      .catch(() => { if (alive) setUploadToken(null) })
    return () => { alive = false }
  }, [getToken])

  const uploadMutation = useMutation({
    mutationFn: async (files: FileList) => {
      const token = await getToken()
      setUploading(true)
      setUploadError(null)

      // Compress each image client-side: typical 5 MB phone photo → ~300–500 KB JPEG.
      // Skip compression for non-image types or files already under 500 KB.
      const compressed: File[] = []
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/') || file.size < 500 * 1024) {
          compressed.push(file)
          continue
        }
        try {
          const out = await imageCompression(file, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            fileType: 'image/jpeg',
          })
          compressed.push(new File([out], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }))
        } catch {
          compressed.push(file) // fall back to original on compression failure
        }
      }

      // Multer config on this route expects the file field to be named 'file'
      // (standardized across Photos/Documents/Drawing-Markup endpoints).
      const formData = new FormData()
      compressed.forEach(f => formData.append('file', f))
      formData.append('phase', uploadPhase)
      if (description) formData.append('description', description)

      const res = await apiFetch(`/api/projects/${projectId}/photos`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.detail || `Upload failed (${res.status})`)
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos', projectId] })
      setUploading(false)
      setDescription('')
      setUploadError(null)
    },
    onError: (err: Error) => {
      setUploading(false)
      setUploadError(err.message)
    },
  })

  // Shared delete handler — called from both the hover-only
  // card trash icon AND the tap-friendly button inside the
  // detail modal so mobile users can delete a photo without
  // ever needing hover. Confirms before destructive action.
  const deletePhoto = async (photoId: string) => {
    const confirmMsg = isEs ? '¿Eliminar esta foto?' : 'Delete this photo?'
    if (typeof window !== 'undefined' && !window.confirm(confirmMsg)) return
    try {
      const token = await getToken()
      await apiFetch(`/api/projects/${projectId}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      queryClient.invalidateQueries({ queryKey: ['photos', projectId] })
      setSelectedPhoto((cur) => (cur?.id === photoId ? null : cur))
    } catch { /* silent */ }
  }

  // Edit photo metadata (phase + description). Calls the new
  // PATCH route added in this PR. Empty description / empty phase
  // are sent as empty strings — the server interprets those as
  // "clear" so the user can wipe a description they no longer
  // want. Query invalidation uses the same ['photos', projectId]
  // key as the list so the card refreshes (and naturally moves
  // between phase filters if phase changed).
  const editMutation = useMutation({
    mutationFn: async ({ photoId, payload }: {
      photoId: string
      payload: { phase: string; description: string }
    }) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/photos/${photoId}`, {
        method: 'PATCH',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.detail || (isEs ? 'No se pudo guardar' : 'Could not save'))
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos', projectId] })
      setEditingPhoto(null)
      setEditError(null)
      // Close the detail modal too — its underlying photo is now
      // stale and the user has already seen the result via the
      // refreshed grid.
      setSelectedPhoto(null)
    },
    onError: (err: Error) => {
      setEditError(err.message)
    },
  })

  // Toggle portal visibility. PRODUCT DECISION, declared: the mark lives in
  // the detail modal (the only touch path, same as Edit/Delete) plus a
  // persistent eye badge on the card so the builder sees at a glance what is
  // published. Default CLOSED — a photo is invisible to the client until this
  // explicit act (Maria 2026-07-30).
  const visibilityMutation = useMutation({
    mutationFn: async ({ photoId, clientVisible }: { photoId: string; clientVisible: boolean }) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/photos/${photoId}`, {
        method: 'PATCH',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clientVisible }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.detail || (isEs ? 'No se pudo cambiar la visibilidad' : 'Could not change visibility'))
      }
      return res.json()
    },
    onSuccess: (updated: Photo) => {
      queryClient.invalidateQueries({ queryKey: ['photos', projectId] })
      // Keep the open modal in sync instead of closing it — the builder is
      // often marking several photos in a row.
      setSelectedPhoto(prev => (prev && prev.id === updated.id ? { ...prev, clientVisible: updated.clientVisible } : prev))
    },
  })

  const filtered = selectedPhase
    ? photos?.filter(p => p.phase === selectedPhase)
    : photos

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Progress Photos')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {photos?.length || 0} {t('photos uploaded')}
          </p>
        </div>
        {/* Connection status */}
        <div className={`flex items-center gap-1.5 text-[10px] font-[700] px-3 py-1.5 rounded-full ${isOnline ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
          {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
          {isOnline ? t('Connected via WiFi') : t('Offline')}
        </div>
      </div>

      {/* Upload zone
          Demo branch (PR #138A): demo projects have no DB row, so a POST
          to /api/projects/:id/photos would 404. Render the shared
          saved-project notice instead — no file input, no mutation. */}
      {isDemo ? (
        <SavedProjectOnlyNotice
          testId="photos-upload-demo-notice"
          bodyEn="Photo uploads are available in saved projects. Create or open a real project to upload progress photos."
          bodyEs="La carga de fotos está disponible en proyectos guardados. Crea o abre un proyecto real para subir fotos de avance."
        />
      ) : (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <select value={uploadPhase} onChange={e => setUploadPhase(e.target.value)}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]">
              {PHASE_OPTIONS.map(p => (
                <option key={p.value} value={p.value}>{isEs ? p.es : p.en}</option>
              ))}
            </select>
            <input value={description} onChange={e => setDescription(e.target.value)}
              placeholder={t('Photo description')}
              className="flex-1 border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-[#ECEAE4] rounded-xl py-4 cursor-pointer hover:border-[#06539B] transition-all"
            >
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={e => { if (e.target.files?.length) uploadMutation.mutate(e.target.files) }} className="hidden" />
              {uploading ? (
                <span className="text-[12px] font-[700]">{t('Uploading...')}</span>
              ) : (
                <>
                  <Upload size={18} className="text-[#9E9890]" />
                  <span className="text-[12px] font-[700]">{t('Upload photos')}</span>
                </>
              )}
            </button>
            {/* Camera capture is only meaningful on phones (the file input's
                `capture` attribute has no effect on desktop, where it just opens a
                file picker identical to Upload). Show on mobile, hide at md+ — the
                same breakpoint the rest of the app uses for mobile-only controls
                (md:hidden). The Upload button is `flex-1`, so it fills the row
                cleanly on desktop with no awkward empty space. */}
            <button
              onClick={() => { fileRef.current?.setAttribute('capture', 'environment'); fileRef.current?.click() }}
              className="flex md:hidden items-center justify-center gap-2 bg-[#06539B] text-white px-6 rounded-xl"
            >
              <Camera size={18} />
              <span className="text-[12px] font-[800]">{t('Take Photo')}</span>
            </button>
          </div>
          {!isOnline && (
            <div className="flex items-center gap-2 text-[10px] font-[700] text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              <WifiOff size={12} />
              {t('Photo saved offline. Will upload when connected to WiFi.')}
            </div>
          )}
          {uploadError && (
            <div className="text-[10px] font-[700] text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {uploadError}
            </div>
          )}
        </div>
      )}

      {/* Phase filter */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setSelectedPhase(null)}
          className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border transition-colors ${!selectedPhase ? 'bg-[#06539B] text-white border-[#06539B]' : 'border-[#ECEAE4] text-[#9E9890]'}`}>
          {t('All')}
        </button>
        {PHASE_OPTIONS.map(p => (
          <button key={p.value} onClick={() => setSelectedPhase(p.value)}
            className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border transition-colors ${selectedPhase === p.value ? 'text-white border-transparent' : 'border-[#ECEAE4] text-[#9E9890]'}`}
            style={selectedPhase === p.value ? { background: PHASE_COLORS[p.value] } : {}}>
            {isEs ? p.es : p.en}
          </button>
        ))}
      </div>

      {/* Photo grid.
          Whole card is a tap target that opens the detail modal —
          the modal is where users on touch devices reach Edit /
          Delete / full description, because hover-only affordances
          do not exist on phones/tablets. The hover-revealed
          pencil + trash icons on the card are an extra desktop
          convenience layered on top of the modal path; they are
          NOT the only way to reach those actions. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {filtered?.map(photo => {
          const phaseLabel = getPhaseLabel(photo.phase, isEs)
          return (
          <button
            type="button"
            key={photo.id}
            onClick={() => setSelectedPhoto(photo)}
            aria-label={isEs ? 'Ver foto' : 'View photo'}
            className="relative group rounded-xl overflow-hidden bg-[#F2F0EB] aspect-square text-left focus:outline-none focus:ring-2 focus:ring-[#06539B]"
          >
            <img
              src={buildPhotoUrl(photo.thumbnailPath || photo.storagePath, uploadToken)}
              alt={photo.description || (isEs ? 'Foto de progreso' : 'Progress photo')}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {/* Desktop convenience: hover-revealed pencil + trash.
                These are NOT the only way to reach Edit / Delete —
                the detail modal opened by tapping the card always
                exposes them as visible buttons (mobile path). */}
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation()
                startEditPhoto(photo)
              }}
              className="absolute top-2 right-10 p-1.5 rounded-full bg-black/40 hover:bg-[#06539B] opacity-0 group-hover:opacity-100 transition-all z-10 cursor-pointer"
              title={isEs ? 'Editar' : 'Edit'}
              aria-hidden="true"
            >
              <Pencil size={12} className="text-white" />
            </span>
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation()
                deletePhoto(photo.id)
              }}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/40 hover:bg-red-500 opacity-0 group-hover:opacity-100 transition-all z-10 cursor-pointer"
              title={isEs ? 'Eliminar' : 'Delete'}
              aria-hidden="true"
            >
              <Trash2 size={12} className="text-white" />
            </span>
            {photo.clientVisible && (
              <span
                className="absolute top-2 left-2 p-1.5 rounded-full bg-[#06539B]/90 z-10"
                title={isEs ? 'Visible al cliente' : 'Visible to client'}
              >
                <Eye size={12} className="text-white" />
              </span>
            )}
            {/* Persistent bottom overlay — caption + human phase
                label, never `F3`. Caption stays line-clamped on
                the card; full text lives in the detail modal. */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
              {phaseLabel && (
                <span className="inline-block text-[9px] font-[800] px-2 py-0.5 rounded-full text-white max-w-full truncate"
                  style={{ background: PHASE_COLORS[photo.phase!] || '#9E9890' }}
                  title={phaseLabel}
                >
                  {phaseLabel}
                </span>
              )}
              {photo.description && (
                <p className="text-[10px] font-[600] text-white mt-1 line-clamp-2">
                  {photo.description}
                </p>
              )}
              <p className="text-[8px] text-white/70 mt-0.5">
                {new Date(photo.createdAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US')}
              </p>
            </div>
          </button>
          )
        })}
      </div>

      {(!filtered || filtered.length === 0) && !isLoading && (
        <div className="text-center py-12">
          <Image size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{t('No photos yet')}</p>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-1">{t('Take or upload progress photos')}</p>
        </div>
      )}

      {/* Detail modal — opens when a card is tapped/clicked.
          This is the only path on touch devices to reach Edit /
          Delete / the full untruncated description, since the
          card's hover-revealed icons never appear on mobile.
          Width caps at max-w-2xl on desktop but stretches edge-
          to-edge on phones; image height is bounded by viewport;
          description column scrolls when long so the modal never
          exceeds the screen. */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-30 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
          onClick={() => setSelectedPhoto(null)}
          role="dialog"
          aria-modal="true"
          aria-label={isEs ? 'Detalle de foto' : 'Photo detail'}
        >
          <div
            className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl overflow-hidden max-h-[95vh] sm:max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#ECEAE4]">
              <h3 className="text-[14px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
                {getPhaseLabel(selectedPhoto.phase, isEs) || (isEs ? 'Foto' : 'Photo')}
              </h3>
              <button
                onClick={() => setSelectedPhoto(null)}
                aria-label={isEs ? 'Cerrar' : 'Close'}
                className="p-2 rounded-full hover:bg-[#F2F0EB]"
              >
                <X size={18} className="text-[#1C1A17]" />
              </button>
            </div>
            <div className="bg-black flex items-center justify-center">
              <img
                src={buildPhotoUrl(selectedPhoto.storagePath, uploadToken)}
                alt={selectedPhoto.description || (isEs ? 'Foto de progreso' : 'Progress photo')}
                className="max-h-[50vh] sm:max-h-[60vh] w-auto max-w-full object-contain"
              />
            </div>
            <div className="px-4 py-3 space-y-2 overflow-y-auto">
              {getPhaseLabel(selectedPhoto.phase, isEs) && (
                <span
                  className="inline-block text-[10px] font-[800] px-2.5 py-0.5 rounded-full text-white"
                  style={{ background: PHASE_COLORS[selectedPhoto.phase!] || '#9E9890' }}
                >
                  {getPhaseLabel(selectedPhoto.phase, isEs)}
                </span>
              )}
              <p className="text-[10px] font-[600] text-[#9E9890]">
                {new Date(selectedPhoto.createdAt).toLocaleDateString(isEs ? 'es-MX' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              {selectedPhoto.description ? (
                <p className="text-[13px] font-[600] text-[#1C1A17] whitespace-pre-wrap">
                  {selectedPhoto.description}
                </p>
              ) : (
                <p className="text-[12px] font-[600] text-[#9E9890] italic">
                  {isEs ? 'Sin descripción.' : 'No description.'}
                </p>
              )}
            </div>
            <div className="px-3 pt-3">
              <button
                onClick={() => {
                  if (!selectedPhoto) return
                  visibilityMutation.mutate({ photoId: selectedPhoto.id, clientVisible: !selectedPhoto.clientVisible })
                }}
                disabled={visibilityMutation.isPending}
                className={`w-full flex items-center justify-center gap-1.5 rounded-full py-2.5 text-[12px] font-[800] border ${
                  selectedPhoto.clientVisible
                    ? 'bg-[#06539B] text-white border-[#06539B]'
                    : 'border-[#ECEAE4] text-[#9E9890] hover:bg-[#F2F0EB]'
                }`}
                aria-pressed={!!selectedPhoto.clientVisible}
                aria-label={isEs ? 'Visible al cliente' : 'Visible to client'}
              >
                {selectedPhoto.clientVisible ? <Eye size={14} /> : <EyeOff size={14} />}
                {visibilityMutation.isPending
                  ? (isEs ? 'Guardando…' : 'Saving…')
                  : selectedPhoto.clientVisible
                    ? (isEs ? 'Visible al cliente' : 'Visible to client')
                    : (isEs ? 'Oculta al cliente' : 'Hidden from client')}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 p-3 border-t border-[#ECEAE4]">
              <button
                onClick={() => {
                  if (!selectedPhoto) return
                  // Open the existing PR #102 edit modal for this
                  // photo. Detail modal stays mounted underneath;
                  // editMutation.onSuccess will close both.
                  startEditPhoto(selectedPhoto)
                }}
                className="flex items-center justify-center gap-1.5 bg-[#06539B] text-white rounded-full py-2.5 text-[12px] font-[800]"
                aria-label={isEs ? 'Editar' : 'Edit'}
              >
                <Pencil size={14} />
                {isEs ? 'Editar' : 'Edit'}
              </button>
              <button
                onClick={() => {
                  if (!selectedPhoto) return
                  deletePhoto(selectedPhoto.id)
                }}
                className="flex items-center justify-center gap-1.5 border border-red-200 text-red-600 rounded-full py-2.5 text-[12px] font-[800] hover:bg-red-50"
                aria-label={isEs ? 'Eliminar' : 'Delete'}
              >
                <Trash2 size={14} />
                {isEs ? 'Eliminar' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit metadata modal — opens when the pencil icon on a
          card is clicked. Edits only the two whitelisted fields
          (phase + description) and PATCHes the server. Cancel
          closes without mutating; Save invalidates the same
          ['photos', projectId] query the list uses so the card
          (and its filter membership) refreshes on close. */}
      {editingPhoto && (
        <div
          className="fixed inset-0 z-40 flex items-end md:items-center justify-center bg-black/40 p-4"
          onClick={cancelEditPhoto}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-[14px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
                {isEs ? 'Editar foto' : 'Edit photo'}
              </h3>
            </div>
            <img
              src={buildPhotoUrl(editingPhoto.thumbnailPath || editingPhoto.storagePath, uploadToken)}
              alt={editingPhoto.description || ''}
              className="w-full h-32 object-cover rounded-lg"
            />
            <div>
              <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
                {isEs ? 'Fase' : 'Phase'}
              </label>
              <select
                value={editPhase}
                onChange={(e) => setEditPhase(e.target.value)}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              >
                {PHASE_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{isEs ? p.es : p.en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
                {isEs ? 'Descripción' : 'Description'}
              </label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={isEs ? 'Descripción de la foto' : 'Photo description'}
                rows={3}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B] resize-none"
              />
            </div>
            {editError && (
              <div className="text-[11px] font-[700] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {editError}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                onClick={cancelEditPhoto}
                className="border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890] hover:bg-[#F2F0EB]"
              >
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={() => {
                  setEditError(null)
                  editMutation.mutate({
                    photoId: editingPhoto.id,
                    payload: { phase: editPhase, description: editDescription },
                  })
                }}
                disabled={editMutation.isPending}
                className="bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] hover:bg-[#14457E] disabled:opacity-50"
              >
                {editMutation.isPending
                  ? (isEs ? 'Guardando…' : 'Saving…')
                  : (isEs ? 'Guardar' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
